/**
 * Felles beregningsmotor. Eneste sted KPI-er beregnes.
 * Uavrundede verdier internt; avrunding kun ved visning.
 * Forholdstall = summert teller / summert nevner (aldri gjennomsnitt av dagsforholdstall).
 */
import type {
  ActivityAllocation,
  Dataset,
  DayKind,
  FieldCoverage,
  LineStateInterval,
  MachineInterval,
  ProductionInterval,
  Rates,
} from '../domain/types';
import { eachDay, hoursBetween, osloDateOf, osloDayBoundsUtc, osloRangeUtc, overlapHours } from '../domain/time';

export type Scope = 'production' | 'all';

export interface MetricQuery {
  siteId: string;
  lineId: string;
  fromDate: string;
  toDate: string;
  scenarioId: string;
  scope: Scope;
  machineIds?: string[];
  fractionId?: string | null;
}

export type MetricStatus = 'ok' | 'unavailable' | 'partial';
export type UnavailableReason =
  | 'no_production'
  | 'incomplete'
  | 'fraction_filter'
  | 'no_engine_hours'
  | 'no_production_time'
  | 'no_data'
  | 'no_line_state'
  | 'planned_off';

/** Opphav for et måltall: rapportert av kilde, estimert fra andre målinger, eller blandet. */
export type Provenance = 'reported' | 'estimated' | 'mixed';

export interface MetricPart {
  label: string;
  value: number;
  unit: string;
  recordIds: string[];
  partial?: boolean;
}

export interface Metric {
  key: string;
  label: string;
  unit: string;
  decimals: number;
  value: number | null;
  status: MetricStatus;
  reason?: UnavailableReason;
  message?: string;
  missingFields?: string[];
  numerator?: MetricPart;
  denominator?: MetricPart;
  note?: string;
  isIllustrative?: boolean;
  isShare?: boolean;
  provenance?: Provenance;
}

/**
 * CO₂-grunnlag med eksplisitt kildeprioritering per intervall:
 * 1) rapportert CO₂ fra telematikk (VisionLink) når feltet finnes,
 * 2) ellers estimat = diesel × faktor når diesel finnes,
 * 3) ellers mangler. Rapportert og estimert legges ALDRI sammen for samme intervall.
 */
export interface Co2Breakdown {
  totalKg: Metric;
  perTonne: Metric;
  reportedKg: number;
  estimatedKg: number;
  reportedIntervals: number;
  estimatedIntervals: number;
  missingIntervals: number;
  totalIntervals: number;
  source: Provenance | 'unavailable';
  sourceLabel: string;
  factor: number;
  factorUnit: string;
  factorSource: string;
  method: string;
  priorityRule: string;
}

export interface Warning {
  code: string;
  severity: 'high' | 'medium' | 'info';
  message: string;
  recordIds?: string[];
  field?: string;
  machineId?: string;
  date?: string;
}

export interface MachineAggregate {
  machineId: string;
  name: string;
  type: string;
  activity: string;
  inScope: boolean;
  engineHours: number;
  idleHours: number;
  fuelLitres: number;
  idleFuelMeasured: number | null;
  idleShare: number | null;
  fuelShare: number | null;
  idleShareOfTotal: number | null;
  fuelPerTotalTonne: number | null;
  co2Kg: number | null;
  co2Source: Provenance | 'unavailable';
  allocationFraction: number;
  allocationMethod: 'measured' | 'demo_assumption' | 'none';
  quality: 'complete' | 'partial' | 'quarantined' | 'none';
  missingFields: string[];
  intervalIds: string[];
  intervalCount: number;
  quarantinedCount: number;
  missingCount: number;
}

export interface DayAggregate {
  date: string;
  kind: DayKind | 'no_data';
  tonnes: number;
  tonnesByFraction: Record<string, number>;
  fuelLitres: number;
  fuelComplete: boolean;
  engineHours: number;
  engineComplete: boolean;
  idleHours: number;
  idleComplete: boolean;
  /** null = linjetilstand ikke målt for dagen (aldri «0 h» når kilden mangler). */
  productionHours: number | null;
  stopHours: number | null;
  lineStateMeasured: boolean;
  co2Kg: number | null;
  litresPerTonne: number | null;
  idleShare: number | null;
  productionRate: number | null;
  coverage: 'complete' | 'partial' | 'planned_off' | 'no_data';
  missingFields: string[];
  hasProductionRecords: boolean;
}

export interface AllocatedInterval {
  interval: MachineInterval;
  fraction: number;
  method: 'measured' | 'demo_assumption' | 'none';
  engineHours: number | null;
  idleHours: number | null;
  fuelLitres: number | null;
}

export interface PeriodResult {
  query: MetricQuery;
  fromUtc: string;
  toUtc: string;
  dates: string[];
  days: DayAggregate[];
  productionDays: number;
  plannedOffDays: number;
  tonnes: Metric;
  tonnesByFraction: Record<string, number>;
  engineHours: Metric;
  idleHours: Metric;
  fuelLitres: Metric;
  productionHours: Metric;
  stopHours: Metric;
  dieselPerTonne: Metric;
  machineHoursPerTonne: Metric;
  idleShare: Metric;
  productionRate: Metric;
  costPerTonne: Metric;
  co2PerTonne: Metric;
  co2TotalKg: Metric;
  co2: Co2Breakdown;
  machines: MachineAggregate[];
  coverage: FieldCoverage[];
  warnings: Warning[];
  quarantined: MachineInterval[];
  productionIntervals: ProductionInterval[];
  lineStates: LineStateInterval[];
  lineStateMeasured: boolean;
  stops: LineStateInterval[];
  allocated: AllocatedInterval[];
  contextLoads: { båt: number; bil: number; recordIds: string[] };
  basisComplete: boolean;
  missingFields: string[];
  rates: Rates;
  scopeLabel: string;
  machineFilterActive: boolean;
  fractionFilterActive: boolean;
}

const EPS = 1e-9;

export function buildQuery(partial: Partial<MetricQuery> & { fromDate: string; toDate: string; scenarioId: string }): MetricQuery {
  return {
    siteId: partial.siteId ?? 'dimmelsvik',
    lineId: partial.lineId ?? 'linje-1',
    scope: partial.scope ?? 'production',
    machineIds: partial.machineIds && partial.machineIds.length ? partial.machineIds : undefined,
    fractionId: partial.fractionId ?? null,
    fromDate: partial.fromDate,
    toDate: partial.toDate,
    scenarioId: partial.scenarioId,
  };
}

function metric(key: string, label: string, unit: string, decimals: number, extra: Partial<Metric> = {}): Metric {
  return { key, label, unit, decimals, value: null, status: 'unavailable', ...extra };
}

function okMetric(key: string, label: string, unit: string, decimals: number, value: number, extra: Partial<Metric> = {}): Metric {
  return { key, label, unit, decimals, value, status: 'ok', ...extra };
}

function unavailable(key: string, label: string, unit: string, decimals: number, reason: UnavailableReason, message: string, extra: Partial<Metric> = {}): Metric {
  return { key, label, unit, decimals, value: null, status: 'unavailable', reason, message, ...extra };
}

/** Validerer et maskinintervall. Returnerer liste av karantenegrunner (tom = gyldig). */
export function validateMachineInterval(mi: MachineInterval): string[] {
  const reasons: string[] = [];
  const len = hoursBetween(mi.fromUtc, mi.toUtc);
  if (!(len > 0)) reasons.push('Ugyldig tidsrom (toUtc må være etter fromUtc)');
  if (mi.engineHours !== null && mi.engineHours < 0) reasons.push('Negative motortimer');
  if (mi.idleHours !== null && mi.idleHours < 0) reasons.push('Negative tomgangstimer');
  if (mi.fuelLitres !== null && mi.fuelLitres < 0) reasons.push('Negativt dieselforbruk');
  if (mi.engineHours !== null && mi.idleHours !== null && mi.idleHours > mi.engineHours + EPS) reasons.push('Tomgangstimer større enn motortimer');
  if (mi.engineHours !== null && len > 0 && mi.engineHours > len + EPS) reasons.push('Motortimer større enn intervallets lengde');
  if (mi.quality === 'quarantined') reasons.push('Markert i karantene av kilden');
  return reasons;
}

/** Finner produksjonsallokering for et maskinintervall (tidsvektet). */
function allocationFor(mi: MachineInterval, allocs: ActivityAllocation[], lineId: string, scope: Scope): { fraction: number; method: AllocatedInterval['method']; overAllocated: boolean } {
  const len = hoursBetween(mi.fromUtc, mi.toUtc);
  if (len <= 0) return { fraction: 0, method: 'none', overAllocated: false };
  let frac = 0;
  let anyAll = 0;
  let method: AllocatedInterval['method'] = 'none';
  for (const a of allocs) {
    if (a.machineId !== mi.machineId) continue;
    const ov = overlapHours(mi.fromUtc, mi.toUtc, a.fromUtc, a.toUtc);
    if (ov <= 0) continue;
    anyAll += (a.allocationFraction * ov) / len;
    if (a.activity === 'production' && (a.lineId === lineId || a.lineId === null)) {
      frac += (a.allocationFraction * ov) / len;
      if (a.method === 'demo_assumption') method = 'demo_assumption';
      else if (method === 'none') method = 'measured';
    }
  }
  const overAllocated = anyAll > 1 + 1e-6;
  if (scope === 'all') return { fraction: 1, method: method === 'none' ? 'measured' : method, overAllocated };
  return { fraction: Math.min(frac, 1), method, overAllocated };
}

export function computePeriod(ds: Dataset, q: MetricQuery): PeriodResult {
  const { fromUtc, toUtc } = osloRangeUtc(q.fromDate, q.toDate);
  const dates = eachDay(q.fromDate, q.toDate);
  const warnings: Warning[] = [];
  const rates = ds.rates;
  const dayKind = new Map(ds.days.map((d) => [d.date, d.kind] as const));
  const machineById = new Map(ds.machines.map((x) => [x.machineId, x] as const));

  const inRange = (a: string) => a >= fromUtc && a < toUtc;

  /* ---------- produksjon ---------- */
  const productionAll = ds.production.filter((p) => p.lineId === q.lineId && inRange(p.fromUtc));
  const productionValid = productionAll.filter((p) => p.producedTonnes !== null && p.quality !== 'quarantined');
  const fractionFilterActive = !!q.fractionId;
  const productionSel = fractionFilterActive ? productionValid.filter((p) => p.fractionId === q.fractionId) : productionValid;
  const tonnes = productionSel.reduce((a, p) => a + (p.producedTonnes ?? 0), 0);
  const tonnesByFraction: Record<string, number> = {};
  for (const p of productionValid) tonnesByFraction[p.fractionId] = (tonnesByFraction[p.fractionId] ?? 0) + (p.producedTonnes ?? 0);

  /* ---------- linjetilstand ---------- */
  const lineStates = ds.lineStates.filter((s) => s.lineId === q.lineId && s.fromUtc < toUtc && s.toUtc > fromUtc);
  let productionHours = 0;
  let stopHours = 0;
  for (const s of lineStates) {
    const ov = overlapHours(s.fromUtc, s.toUtc, fromUtc, toUtc);
    if (s.state === 'production') productionHours += ov;
    else if (s.state === 'stop') stopHours += ov;
  }
  const stops = lineStates.filter((s) => s.state === 'stop');
  /** Linjetilstand regnes som målt kun når kilden faktisk har poster i perioden. 0 poster = ikke målt, ikke «0 h». */
  const lineStateMeasured = lineStates.length > 0;

  /* ---------- maskinintervaller ---------- */
  const machineFilterActive = !!(q.machineIds && q.machineIds.length);
  const quarantined: MachineInterval[] = [];
  const allocated: AllocatedInterval[] = [];
  const overAllocatedIds: string[] = [];
  for (const mi of ds.machineIntervals) {
    if (!inRange(mi.fromUtc)) continue;
    const machine = machineById.get(mi.machineId);
    if (!machine || machine.siteId !== q.siteId) continue;
    if (machineFilterActive && !q.machineIds!.includes(mi.machineId)) continue;
    const reasons = validateMachineInterval(mi);
    if (reasons.length) {
      quarantined.push(mi);
      warnings.push({
        code: 'QUARANTINE',
        severity: 'medium',
        machineId: mi.machineId,
        recordIds: [mi.id],
        date: osloDateOf(mi.fromUtc),
        message: `${mi.machineId}: intervall ${mi.sourceRecordId} satt i karantene (${reasons.join('; ')}).`,
      });
      continue;
    }
    const { fraction, method, overAllocated } = allocationFor(mi, ds.allocations, q.lineId, q.scope);
    if (overAllocated) overAllocatedIds.push(mi.id);
    if (fraction <= 0) continue;
    allocated.push({
      interval: mi,
      fraction,
      method,
      engineHours: mi.engineHours === null ? null : mi.engineHours * fraction,
      idleHours: mi.idleHours === null ? null : mi.idleHours * fraction,
      fuelLitres: mi.fuelLitres === null ? null : mi.fuelLitres * fraction,
    });
  }
  if (overAllocatedIds.length) {
    warnings.push({
      code: 'ALLOCATION_OVER_1',
      severity: 'high',
      recordIds: overAllocatedIds,
      message: `Allokeringssum over 1 for ${overAllocatedIds.length} intervall(er). Intervallene er avvist fra beregningen.`,
    });
  }
  const allocatedValid = allocated.filter((a) => !overAllocatedIds.includes(a.interval.id));

  let engineHours = 0;
  let idleHours = 0;
  let fuelLitres = 0;
  let engineMissing: string[] = [];
  let idleMissing: string[] = [];
  let fuelMissing: string[] = [];
  let anyEstimatedAllocation = false;
  for (const a of allocatedValid) {
    if (a.method === 'demo_assumption') anyEstimatedAllocation = true;
    if (a.engineHours === null) engineMissing.push(a.interval.id);
    else engineHours += a.engineHours;
    if (a.idleHours === null) idleMissing.push(a.interval.id);
    else idleHours += a.idleHours;
    if (a.fuelLitres === null) fuelMissing.push(a.interval.id);
    else fuelLitres += a.fuelLitres;
  }
  const engineComplete = engineMissing.length === 0;
  const idleComplete = idleMissing.length === 0;
  const fuelComplete = fuelMissing.length === 0;
  const missingFields: string[] = [];
  if (!engineComplete) missingFields.push('engineHours');
  if (!idleComplete) missingFields.push('idleHours');
  if (!fuelComplete) missingFields.push('fuelLitres');

  if (fuelMissing.length) {
    const byMachine = new Map<string, string[]>();
    for (const id of fuelMissing) {
      const mi = allocatedValid.find((a) => a.interval.id === id)!.interval;
      byMachine.set(mi.machineId, [...(byMachine.get(mi.machineId) ?? []), mi.id]);
    }
    for (const [machineId, ids] of byMachine) {
      const first = allocatedValid.find((a) => a.interval.id === ids[0])!.interval;
      warnings.push({
        code: 'MISSING_FUEL',
        severity: 'high',
        field: 'fuelLitres',
        machineId,
        recordIds: ids,
        date: osloDateOf(first.fromUtc),
        message: `${machineId}: ${ids.length} forventet dieselintervall mangler (fuelLitres = null, status «missing»). Samlet L/t, CO₂ og kostanslag er utilgjengelig. Datagapet fylles ikke med null.`,
      });
    }
  }
  if (engineMissing.length) {
    warnings.push({ code: 'MISSING_ENGINE', severity: 'high', field: 'engineHours', recordIds: engineMissing, message: `${engineMissing.length} intervall(er) mangler motortimer.` });
  }
  if (idleMissing.length) {
    warnings.push({ code: 'MISSING_IDLE', severity: 'medium', field: 'idleHours', recordIds: idleMissing, message: `${idleMissing.length} intervall(er) mangler tomgangstimer.` });
  }
  if (anyEstimatedAllocation) {
    warnings.push({ code: 'ALLOCATION_ESTIMATED', severity: 'info', message: 'Deler av maskinforbruket er fordelt med en eksplisitt antatt andel (anslag), ikke målte aktivitetstidsrom.' });
  }

  /* ---------- dagsaggregater ---------- */
  const days: DayAggregate[] = dates.map((date) => {
    const kind = (dayKind.get(date) ?? 'no_data') as DayAggregate['kind'];
    const prod = productionValid.filter((p) => osloDateOf(p.fromUtc) === date);
    const prodSel = fractionFilterActive ? prod.filter((p) => p.fractionId === q.fractionId) : prod;
    const t = prodSel.reduce((a, p) => a + (p.producedTonnes ?? 0), 0);
    const byF: Record<string, number> = {};
    for (const p of prod) byF[p.fractionId] = (byF[p.fractionId] ?? 0) + (p.producedTonnes ?? 0);
    const al = allocatedValid.filter((a) => osloDateOf(a.interval.fromUtc) === date);
    let e = 0, i = 0, f = 0;
    let eC = true, iC = true, fC = true;
    for (const a of al) {
      if (a.engineHours === null) eC = false; else e += a.engineHours;
      if (a.idleHours === null) iC = false; else i += a.idleHours;
      if (a.fuelLitres === null) fC = false; else f += a.fuelLitres;
    }
    /* Linjetid per døgn klippes mot Oslo-døgnet [00:00, 24:00) i UTC – et intervall som krysser midnatt
       fordeles på begge døgn, slik at summen av dagene alltid er lik periodesummen. */
    const dayBounds = osloDayBoundsUtc(date);
    let ph = 0, sh = 0;
    let lsCount = 0;
    for (const s of lineStates) {
      const ov = overlapHours(s.fromUtc, s.toUtc, dayBounds.fromUtc, dayBounds.toUtc);
      if (ov <= 0) continue;
      lsCount++;
      if (s.state === 'production') ph += ov;
      else if (s.state === 'stop') sh += ov;
    }
    const dayLineMeasured = lsCount > 0;
    let dayCo2: number | null = 0;
    for (const a of al) {
      const rep = a.interval.reportedCo2Kg;
      if (rep !== null && rep !== undefined) dayCo2 += rep * a.fraction;
      else if (a.fuelLitres !== null) dayCo2 += a.fuelLitres * rates.co2KgPerLitre;
      else { dayCo2 = null; break; }
    }
    if (al.length === 0) dayCo2 = null;
    const mf: string[] = [];
    if (!eC) mf.push('engineHours');
    if (!iC) mf.push('idleHours');
    if (!fC) mf.push('fuelLitres');
    let coverage: DayAggregate['coverage'] = 'complete';
    if (kind === 'planned_off') coverage = 'planned_off';
    else if (kind === 'no_data' || (prod.length === 0 && al.length === 0)) coverage = 'no_data';
    else if (mf.length) coverage = 'partial';
    return {
      date,
      kind,
      tonnes: t,
      tonnesByFraction: byF,
      fuelLitres: f,
      fuelComplete: fC,
      engineHours: e,
      engineComplete: eC,
      idleHours: i,
      idleComplete: iC,
      productionHours: dayLineMeasured ? ph : null,
      stopHours: dayLineMeasured ? sh : null,
      lineStateMeasured: dayLineMeasured,
      co2Kg: dayCo2,
      litresPerTonne: !fractionFilterActive && fC && t > 0 && al.length > 0 ? f / t : null,
      idleShare: eC && iC && e > 0 ? (i / e) * 100 : null,
      productionRate: dayLineMeasured && ph > 0 ? t / ph : null,
      coverage,
      missingFields: mf,
      hasProductionRecords: prod.length > 0,
    };
  });
  const productionDays = days.filter((d) => d.hasProductionRecords).length;
  const plannedOffDays = days.filter((d) => d.kind === 'planned_off').length;

  /* ---------- maskinaggregater ---------- */
  const machinesAgg: MachineAggregate[] = [];
  const machineIdsConsidered = ds.machines.filter((x) => x.siteId === q.siteId && (!machineFilterActive || q.machineIds!.includes(x.machineId)));
  for (const machine of machineIdsConsidered) {
    const al = allocatedValid.filter((a) => a.interval.machineId === machine.machineId);
    const qn = quarantined.filter((x) => x.machineId === machine.machineId);
    const allInRange = ds.machineIntervals.filter((x) => x.machineId === machine.machineId && inRange(x.fromUtc));
    let e = 0, i = 0, f = 0, idleFuel = 0;
    let idleFuelAny = false;
    const mf = new Set<string>();
    let missingCount = 0;
    let mCo2: number | null = 0;
    let mRep = 0, mEst = 0;
    for (const a of al) {
      if (a.engineHours === null) { mf.add('engineHours'); missingCount++; } else e += a.engineHours;
      if (a.idleHours === null) { mf.add('idleHours'); missingCount++; } else i += a.idleHours;
      if (a.fuelLitres === null) { mf.add('fuelLitres'); missingCount++; } else f += a.fuelLitres;
      if (a.interval.idleFuelLitres !== null) { idleFuelAny = true; idleFuel += a.interval.idleFuelLitres * a.fraction; }
      const rep = a.interval.reportedCo2Kg;
      if (mCo2 !== null) {
        if (rep !== null && rep !== undefined) { mCo2 += rep * a.fraction; mRep++; }
        else if (a.fuelLitres !== null) { mCo2 += a.fuelLitres * rates.co2KgPerLitre; mEst++; }
        else mCo2 = null;
      }
    }
    if (al.length === 0) mCo2 = null;
    const mCo2Source: MachineAggregate['co2Source'] = mCo2 === null ? 'unavailable' : mRep && mEst ? 'mixed' : mRep ? 'reported' : 'estimated';
    const inScope = al.length > 0;
    const fraction = al.length ? al.reduce((s, a) => s + a.fraction, 0) / al.length : 0;
    const method = al.find((a) => a.method === 'demo_assumption') ? 'demo_assumption' : al.length ? 'measured' : 'none';
    let quality: MachineAggregate['quality'] = 'none';
    if (allInRange.length === 0) quality = 'none';
    else if (qn.length && !al.length) quality = 'quarantined';
    else if (mf.size || qn.length) quality = 'partial';
    else quality = 'complete';
    machinesAgg.push({
      machineId: machine.machineId,
      name: machine.fictionalName,
      type: machine.type,
      activity: machine.defaultActivity,
      inScope: machine.inProductionScope && (q.scope === 'all' || inScope),
      engineHours: e,
      idleHours: i,
      fuelLitres: f,
      idleFuelMeasured: idleFuelAny ? idleFuel : null,
      idleShare: !mf.has('engineHours') && !mf.has('idleHours') && e > 0 ? (i / e) * 100 : null,
      fuelShare: null,
      idleShareOfTotal: null,
      fuelPerTotalTonne: !mf.has('fuelLitres') && tonnes > 0 && !fractionFilterActive ? f / tonnes : null,
      co2Kg: mCo2,
      co2Source: mCo2Source,
      allocationFraction: fraction,
      allocationMethod: method,
      quality,
      missingFields: [...mf],
      intervalIds: al.map((a) => a.interval.id),
      intervalCount: allInRange.length,
      quarantinedCount: qn.length,
      missingCount,
    });
  }
  for (const ma of machinesAgg) {
    ma.fuelShare = fuelComplete && fuelLitres > 0 ? (ma.fuelLitres / fuelLitres) * 100 : null;
    ma.idleShareOfTotal = idleComplete && idleHours > 0 ? (ma.idleHours / idleHours) * 100 : null;
  }

  /* ---------- dekning ---------- */
  const expected = allocatedValid.length + quarantined.filter((x) => machineById.get(x.machineId)?.inProductionScope).length;
  const scopeCounts = q.scope === 'all' ? 'maskinintervaller, alle maskiner' : 'maskinintervaller i produksjonsområdet';
  const statusOf = (exp: number, rec: number): FieldCoverage['status'] => (exp === 0 ? 'not_applicable' : rec === exp ? 'complete' : rec === 0 ? 'missing' : 'partial');
  const cov = (field: string, unit: string, missing: string[]): FieldCoverage => {
    const received = allocatedValid.length - missing.length;
    return {
      field,
      unit,
      expected,
      received,
      status: statusOf(expected, received),
      sourceId: 'maskindata',
      countsLabel: scopeCounts,
      note: expected === 0 ? 'Ingen maskinintervaller i perioden.' : received === expected ? undefined : `${expected - received} intervall(er) mangler verdi (null). Tomme felt fylles ikke med 0.`,
    };
  };
  const prodExpected = days.filter((d) => d.kind !== 'planned_off' && d.kind !== 'no_data').length;
  const lineStateDays = days.filter((d) => d.kind !== 'planned_off' && d.kind !== 'no_data' && d.lineStateMeasured).length;
  const excavatorIntervals = allocatedValid.filter((a) => machineById.get(a.interval.machineId)?.type === 'gravemaskin');
  const idleFuelReceived = excavatorIntervals.filter((a) => a.interval.idleFuelLitres !== null).length;
  const coverage: FieldCoverage[] = [
    {
      field: 'producedTonnes',
      unit: 't',
      expected: prodExpected,
      received: productionDays,
      status: statusOf(prodExpected, productionDays),
      sourceId: 'produksjon',
      countsLabel: 'planlagte produksjonsdager med produksjonsposter',
      note: prodExpected === 0 ? 'Ingen planlagte produksjonsdager i perioden.' : undefined,
    },
    {
      field: 'lineState',
      unit: 'h',
      expected: prodExpected,
      received: lineStateDays,
      status: statusOf(prodExpected, lineStateDays),
      sourceId: 'produksjon',
      countsLabel: 'planlagte produksjonsdager med registrert linjetilstand',
      note: !lineStateMeasured && prodExpected > 0 ? 'Linjetilstand er ikke målt i perioden: produksjonstid, stopptid og takt er utilgjengelig – ikke 0 h.' : undefined,
    },
    cov('engineHours', 'h', engineMissing),
    cov('idleHours', 'h', idleMissing),
    cov('fuelLitres', 'L', fuelMissing),
    {
      field: 'idleFuelLitres',
      unit: 'L',
      expected: excavatorIntervals.length,
      received: idleFuelReceived,
      status: statusOf(excavatorIntervals.length, idleFuelReceived),
      sourceId: 'maskindata',
      countsLabel: 'maskinintervaller for gravemaskiner (eneste maskintype med måling)',
    },
  ];

  /* ---------- metrics ---------- */
  const prodIds = productionSel.map((p) => p.id);
  const miIds = allocatedValid.map((a) => a.interval.id);
  const lsIds = lineStates.map((s) => s.id);
  const noProductionRecords = productionAll.length === 0;
  const allPlannedOff = days.length > 0 && days.every((d) => d.kind === 'planned_off');

  const tonnesMetric: Metric = noProductionRecords
    ? unavailable('tonnes', 'Produsert mengde', 't', 0, allPlannedOff ? 'planned_off' : 'no_data', allPlannedOff ? 'Ingen planlagt produksjon i perioden.' : 'Ingen produksjonsposter i valgt periode.', { numerator: { label: 'Produsert', value: 0, unit: 't', recordIds: [] } })
    : okMetric('tonnes', 'Produsert mengde', 't', 0, tonnes, {
        numerator: { label: fractionFilterActive ? `Produsert (fraksjon ${q.fractionId})` : 'Produsert (alle fraksjoner)', value: tonnes, unit: 't', recordIds: prodIds },
        note: 'Kun fra produksjonskilden (knuseverk PLS). Lastet/transportert mengde legges aldri til.',
      });

  const sumMetric = (key: string, label: string, unit: string, value: number, complete: boolean, ids: string[], decimals = 1): Metric =>
    complete
      ? okMetric(key, label, unit, decimals, value, { numerator: { label, value, unit, recordIds: ids } })
      : { ...okMetric(key, label, unit, decimals, value, { numerator: { label, value, unit, recordIds: ids, partial: true } }), status: 'partial', message: 'Registrert, delvis – noen intervaller mangler verdi.' };

  const engineMetric = sumMetric('engineHours', 'Tilordnede maskintimer', 'h', engineHours, engineComplete, miIds);
  const idleMetric = sumMetric('idleHours', 'Tomgangstimer', 'h', idleHours, idleComplete, miIds);
  const fuelMetric = sumMetric('fuelLitres', 'Tilordnet diesel', 'L', fuelLitres, fuelComplete, miIds);
  const lineStateMsg = allPlannedOff ? 'Ingen planlagt produksjon i perioden.' : 'Linjetilstand ikke målt i perioden (ingen poster fra produksjonskilden). Vises ikke som 0 h.';
  const prodHoursMetric = lineStateMeasured
    ? okMetric('productionHours', 'Registrert produksjonstid', 'h', 1, productionHours, { provenance: 'reported', numerator: { label: 'Linjetid i tilstand produksjon', value: productionHours, unit: 'h', recordIds: lsIds } })
    : unavailable('productionHours', 'Registrert produksjonstid', 'h', 1, allPlannedOff ? 'planned_off' : 'no_line_state', lineStateMsg, { missingFields: ['lineState'] });
  const stopHoursMetric = lineStateMeasured
    ? okMetric('stopHours', 'Stopptid', 'h', 1, stopHours, { provenance: 'reported', numerator: { label: 'Linjetid i tilstand stopp', value: stopHours, unit: 'h', recordIds: stops.map((s) => s.id) } })
    : unavailable('stopHours', 'Stopptid', 'h', 1, allPlannedOff ? 'planned_off' : 'no_line_state', lineStateMsg, { missingFields: ['lineState'] });

  const contributionNote = machineFilterActive
    ? 'Maskinfilter aktivt: tallet er maskinens bidrag per samlet produsert tonn, ikke maskinens egen tonnproduktivitet (maskinproduksjon er ikke målt).'
    : undefined;
  const scopeLabel = q.scope === 'all' ? 'Alle maskiner (inkl. utlasting og service – ikke sammenlignbart)' : 'Produksjonsområde: 8 produksjonstilordnede maskiner';

  const ratio = (
    key: string,
    label: string,
    unit: string,
    decimals: number,
    numLabel: string,
    num: number,
    numUnit: string,
    numIds: string[],
    numComplete: boolean,
    numMissingFields: string[],
    extra: Partial<Metric> = {},
  ): Metric => {
    if (fractionFilterActive) {
      return unavailable(key, label, unit, decimals, 'fraction_filter', `Fraksjonsfilter aktivt: maskinforbruk er ikke fordelt per fraksjon. Fraksjonens tonn (${Math.round(tonnes)} t) vises, men ${label.toLowerCase()} kan ikke beregnes.`, extra);
    }
    if (noProductionRecords) {
      return unavailable(key, label, unit, decimals, allPlannedOff ? 'planned_off' : 'no_data', allPlannedOff ? 'Ingen planlagt produksjon.' : 'Ingen produksjonsdata i perioden.', extra);
    }
    if (tonnes <= 0) return unavailable(key, label, unit, decimals, 'no_production', 'Ingen produksjon (0 t) – forholdstall kan ikke beregnes.', extra);
    if (!numComplete) {
      return unavailable(key, label, unit, decimals, 'incomplete', `Ufullstendig grunnlag: ${numMissingFields.join(', ')} mangler i beregningsområdet. Observert sum vises som «registrert, delvis».`, {
        ...extra,
        missingFields: numMissingFields,
        numerator: { label: numLabel, value: num, unit: numUnit, recordIds: numIds, partial: true },
        denominator: { label: 'Produsert mengde', value: tonnes, unit: 't', recordIds: prodIds },
      });
    }
    return okMetric(key, label, unit, decimals, num / tonnes, {
      ...extra,
      numerator: { label: numLabel, value: num, unit: numUnit, recordIds: numIds },
      denominator: { label: 'Produsert mengde', value: tonnes, unit: 't', recordIds: prodIds },
      note: contributionNote ?? extra.note,
    });
  };

  const dieselPerTonne = ratio('dieselPerTonne', 'Diesel per tonn', 'L/t', 3, 'Tilordnet diesel', fuelLitres, 'L', miIds, fuelComplete, ['fuelLitres']);
  const machineHoursPerTonne = ratio('machineHoursPerTonne', 'Maskintimer per tonn', 'h/t', 4, 'Tilordnede maskintimer', engineHours, 'h', miIds, engineComplete, ['engineHours']);

  const costMissing = [...(!fuelComplete ? ['fuelLitres'] : []), ...(!engineComplete ? ['engineHours'] : [])];
  const costNum = fuelLitres * rates.dieselNokPerLitre + engineHours * rates.machineNokPerHour;
  const costPerTonne = ratio('costPerTonne', 'Illustrativ driftskostnad', 'NOK/t', 2, `Diesel × ${rates.dieselNokPerLitre} NOK/L + maskintimer × ${rates.machineNokPerHour} NOK/h`, costNum, 'NOK', miIds, fuelComplete && engineComplete, costMissing, {
    isIllustrative: true,
    note: 'Antatte satser, ikke kundens sats. Omfatter kun diesel og et illustrativt maskinbidrag; personell, strøm, sprengning og vedlikehold er ikke modellert. Ikke reell margin eller komplett kostpris.',
  });
  /* ---------- CO₂: kildeprioritering per intervall, aldri dobbelttelling ---------- */
  const factorText = `${String(rates.co2KgPerLitre).replace('.', ',')} ${rates.co2FactorUnit}`;
  let co2ReportedKg = 0;
  let co2EstimatedKg = 0;
  let co2ReportedN = 0;
  let co2EstimatedN = 0;
  const co2MissingIds: string[] = [];
  const co2ReportedIds: string[] = [];
  const co2EstimatedIds: string[] = [];
  for (const a of allocatedValid) {
    const rep = a.interval.reportedCo2Kg;
    if (rep !== null && rep !== undefined) {
      co2ReportedKg += rep * a.fraction;
      co2ReportedN++;
      co2ReportedIds.push(a.interval.id);
    } else if (a.fuelLitres !== null) {
      co2EstimatedKg += a.fuelLitres * rates.co2KgPerLitre;
      co2EstimatedN++;
      co2EstimatedIds.push(a.interval.id);
    } else {
      co2MissingIds.push(a.interval.id);
    }
  }
  const co2Complete = co2MissingIds.length === 0;
  const co2TotalValue = co2ReportedKg + co2EstimatedKg;
  const co2Source: Co2Breakdown['source'] = !co2Complete || allocatedValid.length === 0 ? 'unavailable' : co2ReportedN && co2EstimatedN ? 'mixed' : co2ReportedN ? 'reported' : 'estimated';
  const co2SourceLabel =
    co2Source === 'reported'
      ? 'Rapportert av telematikk (VisionLink) for alle intervaller'
      : co2Source === 'estimated'
        ? `Estimert fra diesel × ${factorText} for alle intervaller`
        : co2Source === 'mixed'
          ? `Blandet: ${co2ReportedN} intervall(er) rapportert, ${co2EstimatedN} estimert fra diesel`
          : 'Utilgjengelig – intervaller mangler både rapportert CO₂ og diesel';
  const co2Provenance: Provenance | undefined = co2Source === 'unavailable' ? undefined : co2Source;
  const co2Label = `Diesel × ${factorText}${co2ReportedN ? ' + rapportert CO₂' : ''}`;
  const co2Note = `${rates.co2FactorMethod} Faktoren er en forutsetning (${rates.co2FactorSource.split('.')[0]}).`;
  const co2TotalKg: Metric =
    noProductionRecords && allocatedValid.length === 0
      ? unavailable('co2TotalKg', 'CO₂ totalt', 'kg', 0, allPlannedOff ? 'planned_off' : 'no_data', allPlannedOff ? 'Ingen planlagt produksjon.' : 'Ingen maskindata i perioden.')
      : !co2Complete
        ? unavailable('co2TotalKg', 'CO₂ totalt', 'kg', 0, 'incomplete', `Ufullstendig grunnlag: ${co2MissingIds.length} intervall(er) mangler både diesel og rapportert CO₂. Summen vises ikke delvis.`, { missingFields: ['fuelLitres'], provenance: co2Provenance })
        : okMetric('co2TotalKg', 'CO₂ totalt', 'kg', 0, co2TotalValue, {
            provenance: co2Provenance,
            numerator: { label: co2Label, value: co2TotalValue, unit: 'kg', recordIds: [...co2ReportedIds, ...co2EstimatedIds] },
            note: co2Note,
          });
  const co2PerTonne = ratio('co2PerTonne', 'CO₂ per tonn', 'kg/t', 3, co2Label, co2TotalValue, 'kg', [...co2ReportedIds, ...co2EstimatedIds], co2Complete, ['fuelLitres'], {
    provenance: co2Provenance,
    note: co2Note,
  });
  const co2: Co2Breakdown = {
    totalKg: co2TotalKg,
    perTonne: co2PerTonne,
    reportedKg: co2ReportedKg,
    estimatedKg: co2EstimatedKg,
    reportedIntervals: co2ReportedN,
    estimatedIntervals: co2EstimatedN,
    missingIntervals: co2MissingIds.length,
    totalIntervals: allocatedValid.length,
    source: co2Source,
    sourceLabel: co2SourceLabel,
    factor: rates.co2KgPerLitre,
    factorUnit: rates.co2FactorUnit,
    factorSource: rates.co2FactorSource,
    method: rates.co2FactorMethod,
    priorityRule: 'Per intervall: rapportert CO₂ fra telematikk brukes når feltet finnes; ellers estimeres fra diesel × faktor; ellers regnes intervallet som manglende. Rapportert og estimert summeres aldri for samme intervall.',
  };

  let idleShare: Metric;
  if (noProductionRecords && allocatedValid.length === 0) {
    idleShare = unavailable('idleShare', 'Tomgangsandel', '%', 1, allPlannedOff ? 'planned_off' : 'no_data', allPlannedOff ? 'Ingen planlagt produksjon.' : 'Ingen maskindata i perioden.', { isShare: true });
  } else if (!engineComplete || !idleComplete) {
    idleShare = unavailable('idleShare', 'Tomgangsandel', '%', 1, 'incomplete', `Ufullstendig grunnlag: ${missingFields.filter((f) => f !== 'fuelLitres').join(', ')} mangler.`, { isShare: true, missingFields: missingFields.filter((f) => f !== 'fuelLitres') });
  } else if (engineHours <= 0) {
    idleShare = unavailable('idleShare', 'Tomgangsandel', '%', 1, 'no_engine_hours', 'Ingen motortimer (0 h) – tomgangsandel kan ikke beregnes.', { isShare: true });
  } else {
    idleShare = okMetric('idleShare', 'Tomgangsandel', '%', 1, (idleHours / engineHours) * 100, {
      isShare: true,
      numerator: { label: 'Tilordnede tomgangstimer', value: idleHours, unit: 'h', recordIds: miIds },
      denominator: { label: 'Tilordnede motortimer', value: engineHours, unit: 'h', recordIds: miIds },
      note: 'Tomgang inngår i motortimer.',
    });
  }

  let productionRate: Metric;
  if (noProductionRecords) {
    productionRate = unavailable('productionRate', 'Produksjonstakt', 't/h', 0, allPlannedOff ? 'planned_off' : 'no_data', allPlannedOff ? 'Ingen planlagt produksjon.' : 'Ingen produksjonsdata.');
  } else if (!lineStateMeasured) {
    productionRate = unavailable('productionRate', 'Produksjonstakt', 't/h', 0, 'no_line_state', 'Linjetilstand ikke målt i perioden – produksjonstid er ukjent, takt kan ikke beregnes.', { missingFields: ['lineState'] });
  } else if (productionHours <= 0) {
    productionRate = unavailable('productionRate', 'Produksjonstakt', 't/h', 0, 'no_production_time', 'Ingen registrert produksjonstid (0 h) – takt kan ikke beregnes.');
  } else {
    productionRate = okMetric('productionRate', 'Produksjonstakt', 't/h', 0, tonnes / productionHours, {
      numerator: { label: 'Produsert mengde', value: tonnes, unit: 't', recordIds: prodIds },
      denominator: { label: 'Registrert produksjonstid (linjetilstand)', value: productionHours, unit: 'h', recordIds: lineStates.filter((s) => s.state === 'production').map((s) => s.id) },
      note: 'Linjetid telles én gang uavhengig av fraksjoner. Maskintimer er et annet måltall og brukes ikke her.',
    });
  }

  const loads = ds.contextLoads.filter((l) => inRange(l.fromUtc) || (l.date >= q.fromDate && l.date <= q.toDate));
  const contextLoads = {
    båt: loads.filter((l) => l.destination === 'båt').reduce((a, l) => a + l.tonnes, 0),
    bil: loads.filter((l) => l.destination === 'bil').reduce((a, l) => a + l.tonnes, 0),
    recordIds: loads.map((l) => l.id),
  };

  return {
    query: q,
    fromUtc,
    toUtc,
    dates,
    days,
    productionDays,
    plannedOffDays,
    tonnes: tonnesMetric,
    tonnesByFraction,
    engineHours: engineMetric,
    idleHours: idleMetric,
    fuelLitres: fuelMetric,
    productionHours: prodHoursMetric,
    stopHours: stopHoursMetric,
    dieselPerTonne,
    machineHoursPerTonne,
    idleShare,
    productionRate,
    costPerTonne,
    co2PerTonne,
    co2TotalKg,
    co2,
    machines: machinesAgg,
    coverage,
    warnings,
    quarantined,
    productionIntervals: productionAll,
    lineStates,
    lineStateMeasured,
    stops,
    allocated: allocatedValid,
    contextLoads,
    basisComplete: engineComplete && idleComplete && fuelComplete && !noProductionRecords,
    missingFields,
    rates,
    scopeLabel,
    machineFilterActive,
    fractionFilterActive,
  };
}

/** Enkel memoisering per datasett + query (filterrespons < 300 ms). */
const memo = new WeakMap<Dataset, Map<string, PeriodResult>>();
export function computePeriodMemo(ds: Dataset, q: MetricQuery): PeriodResult {
  let m = memo.get(ds);
  if (!m) {
    m = new Map();
    memo.set(ds, m);
  }
  const key = JSON.stringify(q);
  let r = m.get(key);
  if (!r) {
    r = computePeriod(ds, q);
    m.set(key, r);
  }
  return r;
}
